import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BookData, Contributor } from './types';
import { initialState } from './initialState';
import { validateStep1, validateStep2, validateStep3, validateStep4, validateStepPreview } from './validations';
import { createBook, updateBook, fetchBook, updateBookStep, submitBookForReview } from './thunks';

// Shared by fetchBook.fulfilled and createBook.fulfilled — a freshly created book
// must be mapped the exact same way a fetched one is, or server-computed fields
// (requiresPageCountApproval, etc.) never make it into Redux for a book created
// and finished in one sitting (the URL never gains ?id= mid-session, so fetchBook
// never fires to correct it).
const mapBookToBookData = (book: any): BookData => ({
  language: book.language || '', title: book.title || '', subtitle: book.subtitle || '',
  author: book.author || '', contributors: book.contributors || [], hasISBN: !!book.isbn,
  isbn: book.isbn || '', bookSize: book.bookSize || '', bindingType: book.bindingType || '',
  interiorColor: book.interiorColor || '', paperType: book.paperType || '',
  coverLamination: book.coverLamination || '', uploadedFile: book.uploadedFile?.filename || null,
  content: book.content || null,
  coverImage: typeof book.coverImage === 'string' ? book.coverImage : (book.coverImage?.filename || null),
  coverImageSource: book.coverImageSource ?? null,
  coverDesignerState: book.coverDesignerState ?? null,
  addons: { spellcheck: book.addons?.spellcheck || false, coverDesign: book.addons?.coverDesign || false, coverDesignType: book.addons?.coverDesignType || null },
  pageCount: book.pageCount || undefined,
  approvedPageCount: book.approvedPageCount || undefined,
  approvedContentHash: book.approvedContentHash || undefined,
  pageCountApprovalStale: !!book.pageCountApprovalStale,
  requiresPageCountApproval: !!book.requiresPageCountApproval,
});

const bookSlice = createSlice({
  name: 'book',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<string>) => {
      state.currentStep = action.payload;
    },
    setShowSuccessModal: (state, action: PayloadAction<boolean>) => {
      state.showSuccessModal = action.payload;
    },
    updateBookData: (state, action: PayloadAction<Partial<BookData>>) => {
      state.bookData = { ...state.bookData, ...action.payload };
      state.isStep1Complete = validateStep1(state.bookData);
      state.isStep2Complete = validateStep2(state.bookData);
      state.isStep3Complete = validateStep3(state.bookData);
      state.isStepPreviewComplete = validateStepPreview(state.bookData);
      state.isStep4Complete = validateStep4(state.bookData);
    },
    addContributor: (state, action: PayloadAction<Contributor>) => {
      state.bookData.contributors.push(action.payload);
    },
    removeContributor: (state, action: PayloadAction<number>) => {
      state.bookData.contributors = state.bookData.contributors.filter((_, index) => index !== action.payload);
    },
    setStepComplete: (state, action: PayloadAction<{ step: number; isComplete: boolean }>) => {
      const { step, isComplete } = action.payload;
      switch (step) {
        case 1: state.isStep1Complete = isComplete; break;
        case 2: state.isStep2Complete = isComplete; break;
        case 3: state.isStep3Complete = isComplete; break;
        case 4: state.isStep4Complete = isComplete; break;
      }
    },
    validateAndSetStep: (state, action: PayloadAction<string>) => {
      const targetStep = action.payload;
      const stepRequirements: { [key: string]: boolean } = {
        'book-info': true,
        'book-form-factor': state.isStep1Complete,
        'book-formatting': state.isStep1Complete && state.isStep2Complete,
        'book-preview': state.isStep1Complete && state.isStep2Complete && state.isStep3Complete,
        'book-cover': state.isStep1Complete && state.isStep2Complete && state.isStep3Complete && state.isStepPreviewComplete,
        'pricing': state.isStep1Complete && state.isStep2Complete && state.isStep3Complete && state.isStepPreviewComplete && state.isStep4Complete
      };
      if (stepRequirements[targetStep]) state.currentStep = targetStep;
    },
    setBookId: (state, action: PayloadAction<string | null>) => {
      state.bookId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetBookData: (state) => {
      Object.assign(state, initialState);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createBook.pending, (state) => { state.isSaving = true; state.error = null; })
      .addCase(createBook.fulfilled, (state, action) => {
        state.isSaving = false;
        state.error = null;
        const book = action.payload.book;
        state.bookId = book._id;
        // Same full-replace pattern as fetchBook.fulfilled below — the response
        // already echoes back everything that was sent to create it, plus
        // server-computed fields like requiresPageCountApproval.
        state.bookData = mapBookToBookData(book);
        state.isStep1Complete = validateStep1(state.bookData);
        state.isStep2Complete = validateStep2(state.bookData);
        state.isStep3Complete = validateStep3(state.bookData);
        state.isStepPreviewComplete = validateStepPreview(state.bookData);
        state.isStep4Complete = validateStep4(state.bookData);
      })
      .addCase(createBook.rejected, (state, action) => { state.isSaving = false; state.error = action.payload as string; })
      .addCase(updateBook.pending, (state) => { state.isSaving = true; state.error = null; })
      .addCase(updateBook.fulfilled, (state, action) => {
        state.isSaving = false;
        state.error = null;
        // Same class of bug as createBook.fulfilled: the backend silently
        // transforms content on every save (trims empty paragraphs, rewrites
        // base64 images to uploaded URLs) and computes pageCountApprovalStale —
        // none of that reaches Redux unless we sync the response back.
        const book = action.payload?.book;
        if (book) {
          state.bookData = mapBookToBookData(book);
          state.isStep1Complete = validateStep1(state.bookData);
          state.isStep2Complete = validateStep2(state.bookData);
          state.isStep3Complete = validateStep3(state.bookData);
          state.isStepPreviewComplete = validateStepPreview(state.bookData);
          state.isStep4Complete = validateStep4(state.bookData);
        }
      })
      .addCase(updateBook.rejected, (state, action) => { state.isSaving = false; state.error = action.payload as string; })
      .addCase(fetchBook.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(fetchBook.fulfilled, (state, action) => {
        state.isLoading = false;
        const book = action.payload.book;
        state.bookId = book._id;
        state.bookStatus = book.status || 'draft';
        state.reviewNotes = book.reviewNotes || null;
        let currentStep = book.currentStep || 'book-info';
        if (book.status === 'rejected' && currentStep === 'pricing') currentStep = 'book-cover';
        // Content edited after reaching Cover in a prior session made the approval
        // stale — force back to the Preview & Approve step rather than leaving them
        // on Cover with a spine width that no longer matches the real page count.
        // Only relevant for books that require approval — pre-existing books never
        // get pageCountApprovalStale set in the first place.
        if (book.requiresPageCountApproval && book.pageCountApprovalStale && (currentStep === 'book-cover' || currentStep === 'pricing')) {
          currentStep = 'book-preview';
        }
        state.currentStep = currentStep;
        state.bookData = mapBookToBookData(book);
        state.isStep1Complete = validateStep1(state.bookData);
        state.isStep2Complete = validateStep2(state.bookData);
        state.isStep3Complete = validateStep3(state.bookData);
        state.isStepPreviewComplete = validateStepPreview(state.bookData);
        state.isStep4Complete = validateStep4(state.bookData);
        state.error = null;
      })
      .addCase(fetchBook.rejected, (state, action) => { state.isLoading = false; state.error = action.payload as string; })
      .addCase(updateBookStep.pending, (state) => { state.isSaving = true; })
      .addCase(updateBookStep.fulfilled, (state) => { state.isSaving = false; })
      .addCase(updateBookStep.rejected, (state, action) => { state.isSaving = false; state.error = action.payload as string; })
      .addCase(submitBookForReview.pending, (state) => { state.isSaving = true; state.error = null; })
      .addCase(submitBookForReview.fulfilled, (state) => { state.isSaving = false; state.showSuccessModal = true; })
      .addCase(submitBookForReview.rejected, (state, action) => { state.isSaving = false; state.error = action.payload as string; });
  }
});

export const {
  setCurrentStep, setShowSuccessModal, updateBookData, addContributor,
  removeContributor, setStepComplete, validateAndSetStep, setBookId, clearError, resetBookData
} = bookSlice.actions;

export default bookSlice.reducer;
